// R123 (Jesse: "bolster the info we can gather about a WO … here on the
// issues board"): the work-order card in the Issues detail modal, drawn from
// row.dispatch (lib/woCalendar woDispatch: counts, days and states only, so
// the card can never show money). It opens the WoQuickView popover through
// data-act="wo-pop" data-wo="<id>" (IssuesPage binds it); with canEdit the
// quick buttons carry data-focus="who" | "book" | "due" so the popover opens
// on that editor (the board passes it on as WoQuickViewOpts.focus).
//
// CONTRACT (R123 §8.6): woCardHtml(dispatch, { canEdit }).
// Look: the Calendar stay-panel card (soft card, small uppercase label, pills,
// a key / value list, one action row). Tokens only.
import { Formatters } from '../../shared/lib/formatters';
import { ensureCss as ensureWoSyncCss } from './woSync';
import type { WoDispatch } from '../../../../lib/woCalendar.mjs';

const esc = (s: any): string => Formatters.escapeHtml(s == null ? '' : String(s));
const PRIORITY_WORD: Record<string, string> = { emergency: 'Emergency', urgent: 'Urgent', routine: 'Routine', pm: 'PM' };
const STATUS_WORD: Record<string, string> = { open: 'Open', in_progress: 'In progress', done: 'Done', cancelled: 'Cancelled' };
const OWNER_WORD: Record<string, [string, string]> = { needs_approval: ['Waiting on owner', 'warn'], approved: ['Owner approved', 'ok'], declined: ['Owner declined', 'bad'], published: ['On owner portal', ''] };
const DAY = /^\d{4}-\d{2}-\d{2}$/;
/** "Thu Oct 23" for a bare YYYY-MM-DD (never shifted by the device's zone). */
export function wdDay(d: string | null | undefined): string {
  if (!d || !DAY.test(String(d).slice(0, 10))) return '';
  const x = new Date(String(d).slice(0, 10) + 'T00:00:00Z');
  return isNaN(x.getTime()) ? '' : x.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }).replace(',', '');
}

export function woCardHtml(d: WoDispatch | null | undefined, o: { canEdit?: boolean } = {}): string {
  if (!d || !d.id) return '';
  ensureWoSyncCss();
  ensureCss();
  const id = esc(d.id);
  const done = d.status === 'done';
  // ── pills ──
  const pills: string[] = [`<span class="iws-pill${d.status === 'in_progress' ? ' tone-warn' : ''}">${esc(STATUS_WORD[d.status] || d.status)}</span>`];
  if (d.priority && d.priority !== 'routine') pills.push(`<span class="iws-pill${d.urgent ? ' tone-bad' : ''}">${esc(PRIORITY_WORD[d.priority] || d.priority)}</span>`);
  if (!done && d.overdue) pills.push('<span class="iws-pill tone-bad">Overdue</span>');
  const own = d.owner_state ? OWNER_WORD[d.owner_state] : null;
  if (own) pills.push(`<span class="iws-pill${own[1] ? ` tone-${own[1]}` : ''}">${own[0]}</span>`);
  if (d.quote_pending) pills.push('<span class="iws-pill tone-warn">Quote pending</span>');
  if (d.vendor_done && !done) pills.push('<span class="iws-pill iss-woc-info">Vendor says done</span>');
  if (d.all_ticked && !done) pills.push('<span class="iws-pill iss-woc-info">All items ticked</span>');
  if (d.followup) pills.push('<span class="iws-pill tone-warn">Follow-up</span>');
  if (d.timer_on) pills.push('<span class="iws-pill tone-warn">On site now</span>');
  // ── key / value rows ──
  const row = (k: string, v: string, tone = '') => `<div class="iss-woc-row"><span class="iss-woc-k">${k}</span><span class="iss-woc-v${tone ? ` ${tone}` : ''}">${v}</span></div>`;
  const rows: string[] = [];
  rows.push(row('Who', d.who ? `${esc(d.who)}${d.who_kind === 'vendor' ? ' <span class="iss-woc-muted">· vendor</span>' : ''}` : '<span class="iss-woc-warn">Nobody assigned</span>'));
  if (d.booked) {
    const span = d.booked_end && d.booked_end !== d.booked ? ` to ${esc(wdDay(d.booked_end))}` : '';
    rows.push(row('Booked', d.was_booked && !done ? `Was booked ${esc(wdDay(d.booked))}${span}` : `${esc(wdDay(d.booked))}${span}`, d.was_booked && !done ? 'late' : ''));
  } else rows.push(row('Booked', '<span class="iss-woc-muted">No day booked</span>'));
  if (d.due_source === 'checkout') rows.push(row('Due', d.due ? `At next checkout, ${esc(wdDay(d.due))}` : 'At next checkout', d.overdue && !done ? 'late' : ''));
  else if (d.due) rows.push(row('Due', esc(wdDay(d.due)), d.overdue && !done ? 'late' : ''));
  else rows.push(row('Due', '<span class="iss-woc-muted">No due date</span>'));
  if (d.items_n) rows.push(row('Checklist', `${d.items_done}/${d.items_n} done`));
  const act: string[] = [];
  if (d.comments_n) act.push(`${d.comments_n} comment${d.comments_n === 1 ? '' : 's'}`);
  if (d.photos_n) act.push(`${d.photos_n} photo${d.photos_n === 1 ? '' : 's'}`);
  if (d.after_n) act.push(`${d.after_n} after`);
  if (d.guest_ok) act.push('guest access OK');
  if (act.length) rows.push(row('Activity', esc(act.join(' · '))));
  if (d.age_days != null) rows.push(row('Opened', d.age_days === 0 ? 'Today' : `${d.age_days}d ago`));
  // ── actions ──
  const quick = o.canEdit && !done ? [
    ['who', d.who ? 'Change who' : 'Assign'],
    ['book', d.booked ? (d.was_booked ? 'Rebook' : 'Move day') : 'Book a day'],
    ['due', d.due || d.due_source ? 'Change due' : 'Set due'],
  ].map(([f, label]) => `<button type="button" class="btn btn-secondary btn-sm iss-woc-q" data-act="wo-pop" data-wo="${id}" data-focus="${f}" data-can-edit="1">${label}</button>`).join('') : '';
  return `<div class="iws-card iss-wocard" data-wo-card="${id}">
    <div class="iss-woc-h">
      <span class="iss-woc-ic" aria-hidden="true">🛠</span>
      <div class="iss-woc-ht"><div class="iss-woc-eyebrow">${d.role === 'cleaner' ? 'Work order · cleaner' : d.role === 'inspector' ? 'Work order · inspector' : 'Work order'}</div><div class="iss-wocard-t">${esc(d.title)}</div></div>
    </div>
    <div class="iws-pills">${pills.join('')}</div>
    <div class="iss-woc-kv">${rows.join('')}</div>
    <div class="iss-woc-acts">
      <button type="button" class="btn btn-secondary btn-sm iss-woc-open" data-act="wo-pop" data-wo="${id}"${o.canEdit ? ' data-can-edit="1"' : ''}>Open work order</button>
      ${quick}
    </div>
  </div>`;
}

let cssDone = false;
function ensureCss(): void {
  if (cssDone || typeof document === 'undefined') return;
  if (document.getElementById('iss-woc-css')) { cssDone = true; return; }
  const st = document.createElement('style');
  st.id = 'iss-woc-css';
  st.textContent = `
  .iss-wocard { display:flex; flex-direction:column; gap:8px; padding:12px 14px; }
  .iss-woc-h { display:flex; align-items:flex-start; gap:10px; min-width:0; }
  .iss-woc-ic { flex:none; width:32px; height:32px; border-radius:var(--radius-sm); background:var(--accent-soft); color:var(--accent); display:inline-flex; align-items:center; justify-content:center; font-size:15px; }
  .iss-woc-ht { flex:1; min-width:0; }
  .iss-woc-eyebrow { font-size:10.5px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:var(--text-muted); }
  .iss-wocard-t { font-size:13.5px; font-weight:700; color:var(--text); line-height:1.35; overflow-wrap:anywhere; }
  .iss-wocard .iws-pills { margin-top:0; }
  .iws-pill.iss-woc-info { background:var(--accent-soft); color:var(--accent); border-color:var(--accent-border); }
  .iss-woc-kv { border:1px solid var(--border-soft); border-radius:var(--radius-sm); padding:0 10px; }
  .iss-woc-row { display:flex; justify-content:space-between; gap:12px; padding:6px 0; border-bottom:1px solid var(--border-soft); font-size:12.5px; }
  .iss-woc-row:last-child { border-bottom:none; }
  .iss-woc-k { color:var(--text-muted); flex:none; }
  .iss-woc-v { color:var(--text); font-weight:500; text-align:right; min-width:0; overflow-wrap:anywhere; }
  .iss-woc-v.late { color:var(--danger-text); }
  .iss-woc-muted { color:var(--text-faint); font-weight:400; }
  .iss-woc-warn { color:var(--warning-text); font-weight:600; }
  .iss-woc-acts { display:flex; flex-wrap:wrap; gap:6px; }
  .iss-woc-acts .btn { min-height:32px; }
  @media (max-width: 640px) {
    .iss-woc-acts .btn { min-height:36px; flex:1 1 auto; justify-content:center; }
  }
  `;
  document.head.appendChild(st);
  cssDone = true;
}
